import { type Component, type JSX, Show, createSignal, onCleanup } from 'solid-js';
import { FiEdit2, FiTrash2 } from 'solid-icons/fi';

/** How long the pointer must stay on a saved chart before edit / remove appear. */
const SUBMENU_ACTION_HOVER_MS = 2000;

export interface ExploreChartSubmenuRowProps {
  objectName: string;
  active: boolean;
  /** Saved charts the user can add (timeseries, scatter, probability, parallel, and the other builders). */
  actionsEnabled: boolean;
  /** Trash is only offered for charts this user owns. */
  canDelete: boolean;
  title: string;
  onOpen: (event: MouseEvent) => void;
  onEdit: () => void;
  onDelete: () => void;
  children: JSX.Element;
}

const ExploreChartSubmenuRow: Component<ExploreChartSubmenuRowProps> = (props) => {
  const [showActions, setShowActions] = createSignal(false);
  let hoverTimer: ReturnType<typeof setTimeout> | undefined;

  const clearHoverTimer = (): void => {
    if (hoverTimer !== undefined) {
      clearTimeout(hoverTimer);
      hoverTimer = undefined;
    }
  };

  onCleanup(clearHoverTimer);

  const handleMouseEnter = (): void => {
    if (!props.actionsEnabled) return;
    clearHoverTimer();
    hoverTimer = setTimeout(() => setShowActions(true), SUBMENU_ACTION_HOVER_MS);
  };

  const handleMouseLeave = (): void => {
    clearHoverTimer();
    setShowActions(false);
  };

  return (
    <div
      class="menu-item text-sm sidebar-explore-submenu-item"
      classList={{
        active: props.active,
        'sidebar-explore-submenu-item-actions': showActions(),
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        class="sidebar-explore-submenu-open"
        title={props.title}
        onClick={(event) => props.onOpen(event)}
      >
        {props.children}
      </button>
      <Show when={showActions()}>
        <span class="sidebar-explore-submenu-actions">
          <button
            type="button"
            class="sidebar-explore-submenu-action"
            title="Edit chart"
            aria-label={`Edit ${props.objectName}`}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              props.onEdit();
            }}
          >
            <FiEdit2 size={14} />
          </button>
          <Show when={props.canDelete}>
            <button
              type="button"
              class="sidebar-explore-submenu-action sidebar-explore-submenu-action-delete"
              title="Remove chart"
              aria-label={`Remove ${props.objectName}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                props.onDelete();
              }}
            >
              <FiTrash2 size={14} />
            </button>
          </Show>
        </span>
      </Show>
    </div>
  );
};

export default ExploreChartSubmenuRow;
